import React from "react";
import { CheckCircle2, Heart, Shield, Users } from "lucide-react";
import { CTABanner } from "../components/Sections";

const PageHeader = ({ title, subtitle }) => (
  <section className="relative knight-gradient text-white py-20">
    <div className="absolute inset-0 grain-overlay" />
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(244,180,0,0.15),transparent_50%)]" />
    <div className="relative max-w-7xl mx-auto px-4 text-center">
      <p className="text-knight-gold font-bold tracking-[0.3em] text-sm mb-3">{subtitle}</p>
      <h1 className="font-display text-4xl md:text-5xl lg:text-7xl font-bold whitespace-nowrap">{title}</h1>
    </div>
  </section>
);

const About = () => (
  <main>
    <PageHeader title="About Us" subtitle="KNIGHT CREDIT CONSULTANTS" />

    <section className="py-20">
      <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <p className="text-knight-gold font-semibold tracking-widest text-xs mb-3">WHO WE ARE</p>
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-knight-navy mb-5">Empowering financial <em>stability since day one</em></h2>
          <p className="text-knight-navy/75 leading-relaxed mb-4">
            Knight Credit Consultants was founded with a clear mission: to empower individuals on their journey to financial stability through professional, personalized credit solutions. We understand that credit challenges can impact many aspects of life, and we're here to help you regain control and confidence in your financial future.
          </p>
          <ul className="space-y-3 mt-6">
            {[
              "Family-owned, client-first approach",
              "FCRA, FDCPA & CROA compliant practices",
              "Transparent monthly progress reports",
              "No long-term contracts — cancel anytime"
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle2 className="text-knight-gold flex-shrink-0 mt-0.5" size={20} />
                <span className="text-knight-navy/80">{t}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="relative">
          <img
            src="https://customer-assets.emergentagent.com/job_d3492e73-7acd-4186-bd61-8b4a9936405d/artifacts/fmdinr2m_recreate-delete-zingo-team-on-sign-in-ba_2mR6j5Q-UrS9vUV3ltXRUw_JOGHa7-dSxSKfbs0bSPDzw.png"
            alt="The Knight Credit Consultants team presenting credit growth results"
            className="rounded-3xl shadow-2xl w-full h-[500px] object-cover"
          />
        </div>
      </div>
    </section>

    <section className="py-20 subtle-gradient">
      <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-14 items-center">
        <div className="order-2 lg:order-1">
          <div className="relative">
            <div className="absolute -inset-4 bg-knight-gold/20 rounded-3xl -rotate-3" />
            <img
              src="https://customer-assets.emergentagent.com/job_d3492e73-7acd-4186-bd61-8b4a9936405d/artifacts/wbozibcw_recreate-delete-approved-on-the-road_axBgl7JVWsi3L4-h9YGZ-Q_NP859mZJT5K7_3-R1EoI_w_cover.png"
              alt="A happy family with car keys in front of their new car"
              className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover"
            />
            <div className="absolute -bottom-6 -right-6 bg-knight-gold text-knight-navy rounded-2xl shadow-xl p-5 max-w-[230px] hidden sm:block">
              <p className="text-[10.5px] font-bold tracking-[0.22em] mb-1">FROM CREDIT TO KEYS</p>
              <p className="font-display text-base font-bold leading-snug">Helping families unlock the doors that credit closed.</p>
            </div>
          </div>
        </div>
        <div className="order-1 lg:order-2">
          <p className="text-knight-gold font-semibold tracking-widest text-xs mb-3">OUR MISSION</p>
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-knight-navy mb-5">More than fixing credit — <em>building futures</em></h2>
          <p className="text-knight-navy/75 leading-relaxed mb-4">
            Our mission is to provide effective, honest, and compassionate credit repair services. We believe in transparency and integrity, offering guidance every step of the way to help you build a resilient financial foundation.
          </p>
          <p className="text-knight-navy/75 leading-relaxed">
            At Knight Credit Consultants, we don't just fix credit issues; we empower our clients with the knowledge and tools needed to maintain lasting credit health.
          </p>
        </div>
      </div>
    </section>

    <section className="py-20">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-knight-gold font-semibold tracking-widest text-xs mb-3">OUR VALUES</p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-knight-navy text-center whitespace-nowrap">The pillars that guide us</h2>
        </div>

        {/* Auto-scrolling marquee — pauses on hover */}
        <div
          className="relative overflow-hidden group"
          style={{ maskImage: "linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)", WebkitMaskImage: "linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)" }}
        >
          <div className="flex gap-6 w-max animate-marquee group-hover:[animation-play-state:paused]">
            {[...[
              { icon: Shield, title: "Integrity", text: "Every dispute, every conversation, every promise \u2014 handled with honesty." },
              { icon: Heart, title: "Compassion", text: "We treat your story with the empathy and discretion it deserves." },
              { icon: Users, title: "Partnership", text: "You're not a case file. You're a partner in your own credit journey." },
              { icon: CheckCircle2, title: "Results", text: "Measurable progress \u2014 reviewed and reported every 60 days." }
            ], ...[
              { icon: Shield, title: "Integrity", text: "Every dispute, every conversation, every promise \u2014 handled with honesty." },
              { icon: Heart, title: "Compassion", text: "We treat your story with the empathy and discretion it deserves." },
              { icon: Users, title: "Partnership", text: "You're not a case file. You're a partner in your own credit journey." },
              { icon: CheckCircle2, title: "Results", text: "Measurable progress \u2014 reviewed and reported every 60 days." }
            ]].map((v, i) => (
              <div key={i} className="w-[320px] flex-shrink-0 perspective">
                <div className="relative rounded-3xl p-[2px] overflow-hidden bg-gradient-to-br from-knight-gold/40 via-knight-navy/20 to-knight-gold/40 hover:from-knight-gold hover:to-knight-gold transition-all duration-500 group/card">
                  <div className="relative bg-white rounded-[22px] p-7 h-full overflow-hidden">
                    <div className="absolute top-3 right-4 font-display text-7xl font-extrabold text-slate-100 select-none group-hover/card:text-knight-gold/15 transition-colors">
                      0{(i % 4) + 1}
                    </div>
                    <div className="relative inline-flex items-center justify-center mb-5">
                      <div className="absolute inset-0 rounded-2xl bg-knight-gold/25 blur-xl scale-110 opacity-0 group-hover/card:opacity-100 transition-opacity duration-500" />
                      <div className="relative w-14 h-14 rounded-2xl bg-knight-navy text-knight-gold flex items-center justify-center transition-all duration-500 group-hover/card:bg-knight-gold group-hover/card:text-knight-navy group-hover/card:-rotate-6 group-hover/card:scale-110">
                        <v.icon size={26} />
                      </div>
                    </div>
                    <h3 className="relative font-display font-bold text-xl text-knight-navy mb-2">{v.title}</h3>
                    <p className="relative text-sm text-knight-navy/70 leading-relaxed">{v.text}</p>
                    <div className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-knight-gold to-knight-gold/30 w-0 group-hover/card:w-full transition-all duration-500" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <CTABanner />
  </main>
);

export default About;
export { PageHeader };
